import React, {
  InputHTMLAttributes,
  CSSProperties,
  useState,
  useRef,
  useLayoutEffect,
  forwardRef,
  useEffect,
} from 'react';
import { TextareaAutosize, useTheme } from '@mui/material';
import { mergeRefs } from '../../../../utils/global/helper';

interface AutoWidthInputProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  extraWidth?: number | string;
  minWidth?: number | string;
  placeholderIsMinWidth?: boolean;
  placeholderExtraWidth?: number | string;
  value?: string | number;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const sizerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  visibility: 'hidden',
  height: 0,
  overflow: 'scroll',
  whiteSpace: 'pre',
};

/**
 * Automatically sized input field.
 */
const AutoWidthInput = forwardRef<HTMLTextAreaElement, AutoWidthInputProps>(
  (
    {
      extraWidth = 18,
      placeholderExtraWidth = 0,
      wrapperClassName,
      wrapperStyle: wrapperStyleProp,
      placeholderIsMinWidth,
      minWidth = 0,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const sizerRef = useRef<HTMLDivElement | null>(null);
    const placeholderSizerRef = useRef<HTMLDivElement>(null);

    const [ready, setReady] = useState<boolean>(false);
    const [input, setInput] = useState<string>('');

    const theme = useTheme();

    const usedValue = `${props.value ?? input}`;

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setReady(false);
      setInput(e.target.value);

      if (props.onChange) props.onChange(e);

      setTimeout(() => setReady(true), 0);
    };

    /* Copy styles of the input field to the sizer, ensuring that the width of the input adjusts accordingly */
    useLayoutEffect(() => {
      if (inputRef.current && sizerRef.current) {
        const computedStyle = window.getComputedStyle(inputRef.current);

        sizerRef.current.style.fontSize = computedStyle.fontSize;
        sizerRef.current.style.fontFamily = computedStyle.fontFamily;
        sizerRef.current.style.fontWeight = computedStyle.fontWeight;
        sizerRef.current.style.fontStyle = computedStyle.fontStyle;
        sizerRef.current.style.letterSpacing = computedStyle.letterSpacing;
        sizerRef.current.style.textTransform = computedStyle.textTransform;
      }
    }, []);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      const inputEl = inputRef.current;
      if (!ready || !inputEl) return;

      const sizerEl = sizerRef.current;
      const placeholderSizerEl = placeholderSizerRef.current;

      const sizerWidth = sizerEl?.scrollWidth;
      const placeholderWidth = placeholderSizerEl?.scrollWidth;

      if (sizerWidth && usedValue.length) {
        /* If the input field has content, update the sizer to match its width  */

        let width = sizerWidth;

        if (
          placeholderIsMinWidth &&
          placeholderWidth &&
          sizerWidth < placeholderWidth &&
          placeholderSizerEl
        ) {
          width = placeholderWidth;
        }

        if (width < +minWidth) {
          width = +minWidth;
        }

        if (width) {
          const newWidth = width + +extraWidth;
          inputEl.style.width = `${newWidth}px`;
        }
      } else if (
        props.placeholder &&
        placeholderWidth &&
        placeholderIsMinWidth
      ) {
        /* If no input value exists, check for placeholder value and update the sizer accordingly  */
        const newWidth =
          Math.max(+minWidth, placeholderWidth) +
          +extraWidth +
          +placeholderExtraWidth;
        inputEl.style.width = `${newWidth}px`;
      } else if (sizerEl) {
        /* If no input value or placeholder exists, update the sizer to the width of the "minWidth" + "extraWidth" prop (default is 18) */

        const newWidth = +minWidth + +extraWidth;
        inputEl.style.width = `${newWidth}px`;
      } else {
        inputEl.style.width = '0px';
      }
    }, [
      usedValue,
      props.placeholder,
      extraWidth,
      placeholderIsMinWidth,
      minWidth,
      placeholderExtraWidth,
      ready,
    ]);

    const wrapperStyle: CSSProperties = {
      ...wrapperStyleProp,
      position: 'relative',
      display: props.style?.display ?? 'inline-block',
      boxSizing: 'border-box',
      maxWidth: '100%',
    };

    const inputStyle: CSSProperties = {
      boxSizing: 'content-box',
      fontSize: theme.typography.h3.fontSize,
      fontFamily: theme.typography.fontFamily,
      resize: 'none',
      maxWidth: '100%',
      border: 'none',
      padding: 0,
      margin: 0,
      backgroundColor: 'transparent',
      outline: 'none',
      textAlign: 'left',
      ...props.style,
    };

    return (
      <div
        className={wrapperClassName}
        style={wrapperStyle}
        data-testid="wrapper">
        <div
          style={sizerStyle}
          ref={el => {
            sizerRef.current = el;
            setReady(true);
          }}
          data-testid="sizer">
          {usedValue}
        </div>
        <TextareaAutosize
          {...props}
          ref={mergeRefs([inputRef, forwardedRef])}
          value={usedValue}
          style={inputStyle}
          onChange={handleInput}
        />
        {props.placeholder ? (
          <div
            ref={placeholderSizerRef}
            style={sizerStyle}
            data-testid="placeholder-sizer">
            {props.placeholder}
          </div>
        ) : null}
      </div>
    );
  },
);

export default AutoWidthInput;
